import { Component, EventEmitter, Output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CustomModalComponent } from '@/components/custom-modal/custom-modal.component';
import { CustomInputComponent } from '@/components/custom-input/custom-input.component';
import { ApiService } from '@/apiConnection/ApiService';
import { AuthenticationService } from '@/apiConnection/authentication.service';
import { Urls } from '@/apiConnection/urls';
import { Utils } from '@/utils/utils';
import { USERNAME_PATTERN, USERNAME_PATTERN_MESSAGE } from '@/apiConnection/model/user';

/**
 * Self-service "editar perfil" form, opened directly from `LayoutComponent`'s sidebar
 * dropdown ("Mi perfil") via `SAUModalService` - there's no dedicated profile page/route for
 * it. Deliberately not the same component as `pages/users/user-form` (the admin CRUD form) -
 * that one edits an arbitrary user by id and includes `role_id`; this one only ever calls
 * `PUT users/me` (see `AuthController::updateMe`, `erp-backend`), which always operates on the
 * authenticated user's own row and never accepts role_id/company_id, so there's no id/role
 * field here at all.
 */
@Component({
  selector: 'app-profile-form',
  imports: [
    CustomModalComponent,
    ReactiveFormsModule,
    CustomInputComponent,
  ],
  templateUrl: './profile-form.component.html',
})
export class ProfileFormComponent {
  @Output() closeFunction = new EventEmitter<void>();
  @Output() acceptFunction = new EventEmitter<any>();

  saving = false;
  usernameErrors = { pattern: USERNAME_PATTERN_MESSAGE };

  form = new FormGroup({
    name: new FormControl('', [Validators.required]),
    username: new FormControl('', [Validators.pattern(USERNAME_PATTERN)]),
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl(''),
  });

  constructor(
    private apiService: ApiService,
    private authService: AuthenticationService,
  ) {
    const current = this.authService.currentUserValue;
    this.form.patchValue({ name: current?.name, username: current?.username, email: current?.email });
  }

  save() {
    if (this.form.invalid) {
      Utils.notification.alert('Completa todos los campos obligatorios');
      return;
    }
    this.saving = true;

    const body: any = {
      name: this.form.value.name,
      username: this.form.value.username?.trim() || null,
      email: this.form.value.email,
    };
    if (this.form.value.password) {
      body.password = this.form.value.password;
    }

    this.apiService.put(`${Urls.urls.users}/me`, body).subscribe({
      next: (res) => {
        this.authService.updateCurrentUserProfile({ name: body.name, username: body.username, email: body.email });
        Utils.notification.success('Perfil actualizado');
        this.acceptFunction.emit(res);
        this.closeFunction.emit();
      },
      error: (err) => Utils.notification.alert(Utils.extractErrorMessage(err) || 'Error al actualizar el perfil'),
      complete: () => (this.saving = false),
    });
  }
}
